/**
 * Life4Earth V2 — Hook for the bulk per-MP signal data the portfolio
 * Data aggregator needs to color the Schedule + Mission dots on each
 * Mini-Project row.
 *
 * DEV-1116 Phase 1b. Pairs with the new
 * GET /api/mini-projects/portfolio-signals endpoint which returns one
 * row per readable, non-archived MP carrying:
 *   • latestStatus (planned/actual %, reporting date) or null
 *   • goals[]      (goalGuid, includedInRollup, latestAssessment)
 *
 * The App.tsx aggregator then feeds these into
 * computeMiniProjectKpiStatus alongside each MP from useApiMiniProjects.
 *
 * Failure mode: silent. If the fetch errors (auth lost, server down,
 * etc.) we return an empty array — the aggregator falls back to the
 * Phase 1a behavior (DR + lifecycle colors only, others grey).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMiniProjectPortfolioSignals,
  EVT_MINI_PROJECT_MUTATED,
  type MiniProjectPortfolioSignalsRow,
} from "./miniProjects";

export interface UseApiMiniProjectPortfolioSignalsResult {
  signals: MiniProjectPortfolioSignalsRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiMiniProjectPortfolioSignals(): UseApiMiniProjectPortfolioSignalsResult {
  const mountedRef = useRef(true);
  const [state, setState] = useState<{
    signals: MiniProjectPortfolioSignalsRow[];
    loading: boolean;
    error: string | null;
  }>({
    signals: [],
    loading: true,
    error: null,
  });

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await fetchMiniProjectPortfolioSignals();
      if (!mountedRef.current) return;
      setState({ signals: result, loading: false, error: null });
    } catch (err) {
      if (!mountedRef.current) return;
      setState({
        signals: [],
        loading: false,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => { mountedRef.current = false; };
  }, [load]);

  // Task #221. MP mutations elsewhere in the app (status post/edit,
  // quick-update) dispatch EVT_MINI_PROJECT_MUTATED. Refetch the bulk
  // signals so the portfolio grid's Schedule + Mission KPI cells
  // reflect the change. Listener is global by design — any MP edit
  // refreshes all signals — because the bulk endpoint is one round-trip
  // and the grid surfaces every MP at once.
  //
  // 2026-05-27 cross-browser fix — also listen for
  // `l4e_project_updated`, the window event the SignalR connection
  // fires on receipt of a KpiHub `ProjectUpdated` push.
  // MiniProjectStatusesController now broadcasts on every status
  // create/update/quick-update so a second user on a different browser
  // sees the portfolio update without a manual reload. Unconditional
  // refetch on either signal — bulk endpoint is one round-trip.
  useEffect(() => {
    const onMutated = () => { load(); };
    if (typeof window !== "undefined") {
      window.addEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
      window.addEventListener("l4e_project_updated", onMutated);
      return () => {
        window.removeEventListener(EVT_MINI_PROJECT_MUTATED, onMutated);
        window.removeEventListener("l4e_project_updated", onMutated);
      };
    }
    return undefined;
  }, [load]);

  return { ...state, refetch: load };
}
